import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUnifiedShellIntro, putUnifiedShellIntroSeen } from '@upright/ui/api/iam/users';
import { useEffect } from 'react';

const unifiedShellIntroQuery = queryOptions({
  queryKey: ['unifiedShellIntro'],
  queryFn: getUnifiedShellIntro,
  staleTime: Infinity,
});

/**
 * Whether the callout is open, held in the query cache rather than in the top bar that renders it.
 * A first page load renders the top bar twice: once in the frame shown while the page's code loads
 * (`UnifiedShellFrame suspended`) and again in the frame that replaces it. The callout can open in
 * the first and is recorded as seen there, so if the open state belonged to that top bar it would go
 * with it, and the second, reading "already seen", would show nothing. In the cache it outlives the
 * top bar, and whichever top bar is on screen shows it until it is closed. Never fetched: it starts
 * closed and only `useUnifiedShellIntro` sets it.
 */
const calloutOpenQuery = queryOptions({
  queryKey: ['unifiedShellIntro', 'calloutOpen'],
  queryFn: () => false,
  initialData: false,
  staleTime: Infinity,
  gcTime: Infinity,
});

/**
 * The callout at the account menu that tells a user moved to the unified shell where the "New
 * interface" switch is (PLT-8155): whether it is open, and how to close it.
 *
 * The server decides who gets it (`shouldShowUnifiedShellIntro`) and this only asks. It is shown on
 * one page load: the moment it opens it is recorded as seen on the user, so a reload or another
 * device does not show it again, and the cached answer turns false so that it is not opened a second
 * time in this session either. From then on it stays open, on whichever page, until it is closed.
 *
 * `enabled` is false wherever the account menu has no "New interface" row to point at.
 */
export function useUnifiedShellIntro(enabled: boolean): { isOpen: boolean; close: () => void } {
  const queryClient = useQueryClient();
  const { data } = useQuery({ ...unifiedShellIntroQuery, enabled });
  const { data: isOpen } = useQuery(calloutOpenQuery);
  const { mutate: markSeen } = useMutation({ mutationFn: putUnifiedShellIntroSeen });
  const show = data?.show === true;

  useEffect(() => {
    if (!show) {
      return;
    }
    queryClient.setQueryData(calloutOpenQuery.queryKey, true);
    markSeen();
    queryClient.setQueryData(unifiedShellIntroQuery.queryKey, { show: false });
  }, [show, markSeen, queryClient]);

  return {
    // Gated again here because the open state is shared: a top bar with no "New interface" row,
    // such as the Arvo console's, must not show a callout another top bar opened.
    isOpen: enabled && isOpen,
    close: () => {
      queryClient.setQueryData(calloutOpenQuery.queryKey, false);
    },
  };
}
